<template>
  <div>
    <AccountGroupAddModal :open="add_group"
                          @created="handleGroup"
                          @close="add_group = false"/>
    <div>
      <div class="col-span-2
                  grid grid-cols-1 col-gap-12 row-gap-11 items-center
                  lg:grid-cols-3">
        <div class="lg:col-span-3 flex items-center" v-if="!edit_item">
          <div class="text-xl font-bold text-text leading-tight ">{{ $t('formTitle') }}</div>
          <AccountsSpaceSettings class="ml-auto"/>
        </div>
        <BaseSelect
            v-model="type"
            :options="type_list"
            track-by="id"
            label="name"
            :select-label="$t('typeLabel')"
            :allow-empty="false"
            :searchable="false"
            :show-labels="false"
            @input="handleType"/>
        <BaseInput v-model.trim="$v.title.$model"
                   :label="$t('accountNameLabel')"
                   :class="{'--error':$v.title.$error }">
          <template v-slot:errors="">
            <div class="error" v-if="!$v.title.required">{{ $t('errors.required') }}</div>
            <div class="error" v-else-if="!$v.title.maxLength">{{ $t('errors.maxLength', {length: 25}) }}</div>
            <div class="error" v-else-if="!$v.title.noSpecSymbols">{{ $t('errors.noSpecSymbols') }}</div>
          </template>
        </BaseInput>
        <ValueInput v-model.trim="balance"
                    :label="$t('currentBalanceLabel')"
                    placeholder="0.00"
                    allow_negative
                    text_left
                    :currency_id="currency_id.id">
        </ValueInput>
        <div>
          <MoreSettings class="ml-3 px-2 relative z-10"
                        :class="more_settings && 'bg-block'"
                        @click="toggleMoreSettings(!more_settings)"
                        :active="more_settings"/>
        </div>
      </div>
      <div v-if="more_settings"
           class="grid grid-cols-1 lg:col-span-2 lg:grid-cols-2 col-gap-12 row-gap-12 relative
                border rounded border-border
                p-10 -mt-4">
        <div>
          <div class="grid row-gap-11 -mx-0-5 pr-40">
            <IconPicker :selected="icon"
                        :icon-color="icon_color"
                        :icon-bg="icon_bg"
                        @change="handleIconSelect">
              {{ $t('iconPickText') }}
            </IconPicker>
            <IconColorPicker
                v-model="icon_bg"
                :hidden="isBankIconSelected"
                @change="handleIconColor"
            />
            <BaseSelect v-model="currency_id"
                        :options="currenciesSortedList"
                        :label="`name_${$i18n.locale}`"
                        :select-label="$t('currencyLabel')"
                        group-label="title"
                        group-values="list"
                        :group-select="false"
                        :allow-empty="false"
                        :disabled="!!edit_item"
                        :searchable="true"
                        :show-labels="false"
                        @input="handleCurrency"/>
            <div class="relative -mr-40 pr-40">
              <BaseSelect v-model="group_id"
                          :options="account_groups.data"
                          label="title"
                          track-by="id"
                          :select-label="$t('groupSelectLabel')"
                          :allow-empty="false"
                          :searchable="false"
                          :show-labels="false"
                          @input="handleGroup"/>
              <div class="absolute right-0 top-4 m-auto
                      border-b border-dashed border-opacity-50 border-text-ternary
                      text-2sm text-text-ternary hover:text-red hover:border-red
                      transition duration-200 cursor-pointer"
                   @click="add_group = true">
                {{ $t('createGroup') }}
              </div>
            </div>
            <PercentInput v-if="!!type && (type.id == 3 || type.id == 5)"
                          v-model.trim="$v.percent.$model"
                          :label="$t('creditPercentLabel')"
                          text_left
                          :currency_id="currency_id.id"
                          :class="{'--error':$v.percent.$error }">
              <template v-slot:errors="">
                <div class="error" v-if="!$v.percent.required">{{ $t('errors.required') }}</div>
              </template>
            </PercentInput>
          </div>
        </div>
        <div>
          <div class="flex flex-col pt-2 space-y-6">
            <BaseCheckbox :checked="commission"
                          @change="handleCommissionCheck($event)">
              {{ $t('checkCommission') }}
            </BaseCheckbox>
            <div v-if="commission" class="pl-12 -mb-2">
              <BaseRadioGroup :options="commission_type_list"
                              :value="commission_type"
                              @change="commission_type = $event"/>
              <PercentInput v-if="commission_type === 1"
                            v-model.trim="$v.commission_value.$model"
                            :label="$t('commissionAmountLabel')"
                            placeholder="0.00"
                            text_left
                            :class="{'--error':$v.commission_value.$error }">
                <template v-slot:errors="">
                  <div class="error" v-if="!$v.commission_value.required">{{ $t('errors.required') }}</div>
                </template>
              </PercentInput>
              <ValueInput v-else
                          v-model.trim="$v.commission_value.$model"
                          :label="$t('commissionAmountLabel')"
                          placeholder="0.00"
                          :currency_id="currency_id.id"
                          text_left
                          :class="{'--error':$v.commission_value.$error }">
                <template v-slot:errors="">
                  <div class="error" v-if="!$v.commission_value.required">{{ $t('errors.required') }}</div>
                </template>
              </ValueInput>
            </div>
            <BaseCheckbox v-if="fullSettings" :checked="need_check"
                          @change="need_check = $event">
              {{ $t('checkCheckImportLabel') }}
            </BaseCheckbox>
            <BaseCheckbox v-if="fullSettings" :checked="auto_confirm"
                          @change="auto_confirm = $event">
              {{ $t('checkAutoConfirm') }}
            </BaseCheckbox>
            <BaseCheckbox :checked="capitalization"
                          @change="capitalization = $event">
              {{ $t('checkCapitalization') }}
            </BaseCheckbox>
            <BaseCheckbox :checked="group_capitalization"
                          @change="group_capitalization = $event">
              {{ $t('checkGroupCapitalization') }}
            </BaseCheckbox>
            <BaseCheckbox v-if="fullSettings" :checked="is_hidden"
                          @change="is_hidden = $event">
              {{ $t('checkIsHidden') }}
            </BaseCheckbox>
          </div>
        </div>
      </div>
      <div v-if="showMembersPermissions" class="mt-8">
        <MoreSettings class="ml-3 px-2 relative z-10 bg-block md:cursor-default"
                      :text="$t('membersPermissions')"
                      active/>
        <div class="grid grid-cols-1 lg:col-span-2 lg:grid-cols-2 col-gap-12 row-gap-4 relative
                border rounded border-border
                p-10 -mt-4">
          <BaseCheckbox v-for="(member,i) in memberPermissions" :key="i"
                        class="col-span-2"
                        :checked="member.value"
                        @change="memberPermissions[i].value = $event">
            <template v-if="member.username">
              {{ member.username }}
              <span class="ml-2 text-text-ternary">{{ member.email }}</span>
            </template>
            <template v-else>
              {{ member.email }}
            </template>
          </BaseCheckbox>
        </div>
      </div>
      <div v-if="!edit_item" class="grid items-center grid-cols-2 col-gap-12 mt-11 lg:col-span-2">
        <BaseButton @click="addAcc"
                    theme="red"
                    class="lg:w-91"
                    :loading="submitted">
          {{ $t('addAccount') }}
        </BaseButton>
      </div>
      <div v-else class="flex flex-wrap items-center mt-11 lg:col-span-2">
        <BaseButton @click="save"
                    class="w-78 lg:w-91 mr-8"
                    theme="gray"
                    :loading="submitted">
          {{ $t('saveChanges') }}
        </BaseButton>
        <BaseButton @click="$emit('cancelEdit')"
                    :loading="submitted"
                    class="w-50 lg:w-68 mr-20"
                    theme="white">
          {{ $t('cancel') }}
        </BaseButton>
        <AccountArchiveBtn class="ml-auto"
                           :submitted="submitted"
                           @confirm="archive($event)">
          <template v-slot:text>
            {{ $t('removeAccount') }}
          </template>
        </AccountArchiveBtn>
      </div>
    </div>
  </div>
</template>

<script>
import {required, decimal, helpers, maxLength} from 'vuelidate/lib/validators'
import {mapActions, mapGetters, mapState} from "vuex";
import BaseInput from '@/components/UI/BaseInput/BaseInput.vue';
import ValueInput from '@/components/UI/BaseInput/ValueInput.vue';
import PercentInput from '@/components/UI/BaseInput/PercentInput.vue';
import IconPicker from '@/components/common/IconPicker/IconPicker.vue';
import IconColorPicker from '@/components/common/IconPicker/IconColorPicker.vue';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseCheckbox from "@/components/UI/BaseCheckbox";
import MoreSettings from "@/components/common/MoreSettings";
import BaseRadioGroup from "@/components/UI/BaseRadioGroup";
import AccountGroupAddModal from "@/components/cabinet/SpaceSettings/AccountsSettings/AccountGroupAddModal";
import AccountArchiveBtn from "@/components/cabinet/SpaceSettings/AccountsSettings/AccountArchiveBtn";
import AccountsSpaceSettings
  from "@/components/cabinet/SpaceSettings/AccountsSpaceSettings/AccountsSpaceSettings";
import { isBankIcon } from '@/components/icons/MKIcons/icons';
import {
  DEFAULT_ICON_BG,
  DEFAULT_ICON_COLOR,
  colorsForBg,
  isTransparentBg
} from '@/components/icons/MKIcons/iconPalette';

const noSpecSymbols = helpers.regex('noSpecSymbols', /^[^*|\/":<>[\]{}`\\()';@&!$]+$/)

export default {
  name: "AccountsSettingsForm",
  components: {
    AccountsSpaceSettings,
    AccountArchiveBtn,
    BaseRadioGroup,
    MoreSettings,
    BaseCheckbox,
    BaseSelect,
    BaseInput,
    BaseButton,
    ValueInput,
    PercentInput,
    IconPicker,
    IconColorPicker,
    AccountGroupAddModal,
  },
  props: {
    edit_item: {
      type: Object,
      default: null
    },
    fullSettings: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      submitted: false,
      more_settings: !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false,

      add_group: false,
      title: '',
      type: null,
      icon: 'wallet',
      icon_color: DEFAULT_ICON_COLOR,
      icon_bg: DEFAULT_ICON_BG,
      currency_id: {"id": 123, "code": "RUB", "name_en": "Russian Ruble", "name_ru": "Российский рубль"},
      group_id: {id: null, title: this.$i18n.locale === 'en' ? 'Without group' : 'Без группы'},

      balance: null,
      percent: null,

      need_check: false,
      auto_confirm: true,

      commission: false,
      commission_type: null,
      commission_value: null,
      commission_type_list: [
        {label: this.$t('commissionTypePercent'), value: 1},
        {label: this.$t('commissionTypeFix'), value: 2}
      ],

      capitalization: true,
      group_capitalization: true,
      is_hidden: false,

      memberPermissions: []
    }
  },
  validations() {
    if (!this.more_settings) {
      return {
        title: {
          required,
          noSpecSymbols,
          maxLength: maxLength(25)
        }
      }
    } else if (!!this.type && (this.type.id == 3 || this.type.id == 5)) {
      if (this.commission) {
        return {
          title: {
            required,
            noSpecSymbols,
            maxLength: maxLength(25)
          },
          percent: {
            required,
            decimal
          },
          commission_value: {
            required,
          },
        }
      } else {
        return {
          title: {
            required,
            noSpecSymbols,
            maxLength: maxLength(25)
          },
          percent: {
            required,
          },
        }
      }
    } else if (this.commission) {
      return {
        title: {
          required,
          noSpecSymbols,
          maxLength: maxLength(25)
        },
        commission_value: {
          required,
        },
      }
    } else {
      return {
        title: {
          required,
          noSpecSymbols,
          maxLength: maxLength(25)
        },
      }
    }
  },
  computed: {
    ...mapGetters('cabinet/currencies', ['currenciesSortedList', 'currenciesList']),
    ...mapState('space/accounts', ['account_groups']),
    ...mapState('space', ['current_space']),
    ...mapState('cabinet/user', ['user_info']),
    ...mapGetters('cabinet/constants', ['getConstantList']),
    ...mapGetters('space', ['currentUserIsAdmin']),
    type_list() {
      return this.getConstantList('account_type')
    },
    showMembersPermissions() {
      return this.currentUserIsAdmin && this.current_space.data.type === 3;
    },
    isBankIconSelected() {
      return isBankIcon(this.icon);
    },
    permissionMembersList() {
      let memberIsNotCreator = (member) => member.user.id !== this.current_space.data.creator_id,
          memberIsNotCurrent = (member) => member.user.id !== this.user_info.data.id;
      return [...this.current_space.data.members].filter(user => memberIsNotCreator(user) && memberIsNotCurrent(user))
    }
  },
  methods: {
    ...mapActions('cabinet/currencies', ['getCurrencies']),
    ...mapActions('space/accounts', ['getAccounts', 'createAccount', 'updateAccount', 'archiveAccount']),
    toggleMoreSettings(val) {
      localStorage.setItem('settings', val ? '1' : '0');
      this.more_settings = val;
    },
    handleType(val) {
      this.type = val;
    },
    handleIconSelect(val) {
      this.icon = val;
      if (isBankIcon(val)) {
        this.icon_color = null;
        this.icon_bg = null;
      }
    },
    handleIconColor({ bg, color }) {
      this.icon_bg = bg;
      this.icon_color = color;
    },
    handleCurrency(val) {
      this.currency_id = val;
    },
    handleGroup(val) {
      this.group_id = val;
      if (this.add_group) {
        this.add_group = false;
      }
      this.$emit('accountGroupCreated')
    },
    handleCommissionCheck(val) {
      if (!val) {
        this.commission_type = null;
        // this.commission_value = null;
      } else {
        this.commission_type = 1;
        // this.commission_value = '0.00';
      }
      this.commission = val;
    },
    getPayloadData() {
      let commonData = {
        type_id: +this.type.id,
        currency_id: this.currency_id.id,
        title: this.title,
        icon: this.icon,
        icon_color: this.isBankIconSelected ? null : this.icon_color,
        icon_bg: this.isBankIconSelected ? null : this.icon_bg,
        group_id: this.group_id.id,
        balance: this.balance,
        percent: this.percent,
        commission: this.commission,
        commission_type: this.commission_type,
        commission_value: this.commission_value,
        need_check: this.need_check,
        auto_confirm: this.auto_confirm,
        capitalization: this.capitalization,
        group_capitalization: this.group_capitalization,
        is_hidden: this.is_hidden,
      }
      if (this.showMembersPermissions) commonData.permissions = this.getMembersPermissions()
      return commonData
    },
    addAcc() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          space_id: this.current_space.data.id,
          ...this.getPayloadData()
        };
        this.createAccount(data).then(res => {
          if (res.body.success) {
            this.clearFields();
            this.$v.$reset();
            this.$toasted.success('Счёт добавлен')
            this.$emit('accountCreated');
          } else {
            this.$toasted.error('Произошла ошибка')
            console.log(res)
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        document.documentElement.scrollTop = 0;
        this.$toasted.error('Проверьте поля')
      }
    },
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          id: this.edit_item.id,
          ...this.getPayloadData()
        };
        this.updateAccount(data).then(res => {
          if (res.body.success) {
            this.$emit('cancelEdit');
            this.getAccounts();
            this.clearFields();
            this.$v.$reset();
            this.$toasted.success('Изменения счета сохранены')
          } else {
            this.$toasted.error('Проверьте поля')
            console.log(res)
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        document.documentElement.scrollTop = 0;
        this.$toasted.error('Проверьте поля')
      }
    },
    archive(delete_transactions = false) {
      this.submitted = true;
      this.archiveAccount({id: this.edit_item.id, delete_transactions}).then(res => {
        if (res.body.success) {
          this.$emit('cancelEdit');
          this.getAccounts();
          this.clearFields();
          this.$toasted.info('Счет удален')
        } else {
          console.log(res)
          this.$toasted.error('Произошла ошибка')
        }
      }).finally(() => {
        this.submitted = false;
      })
    },
    clearFields() {
      this.title = '';
      this.type = this.type_list[0];
      this.more_settings = !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false;
      this.currency_id = this.currenciesList.filter(i => i.id === this.current_space.data.currency_id)[0];
      this.icon = 'wallet';
      this.icon_color = DEFAULT_ICON_COLOR;
      this.icon_bg = DEFAULT_ICON_BG;
      this.group_id = {id: null, title: 'Без группы'};
      this.balance = null;
      this.percent = null;
      this.need_check = false;
      this.auto_confirm = true;
      this.commission = false;
      this.commission_type = null;
      this.commission_value = null;
      this.capitalization = true;
      this.group_capitalization = true;
      this.is_hidden = false;
      this.$v.$reset();
    },
    setEditFields(item) {
      this.more_settings = true;
      this.title = item.title;
      this.type = this.type_list.filter(i => i.id == item.type_id)[0];
      this.icon = item.icon;
      if (isBankIcon(item.icon) || isTransparentBg(item.icon_bg)) {
        this.icon_color = null;
        this.icon_bg = null;
      } else {
        const pair = colorsForBg(item.icon_bg);
        this.icon_bg = pair.bg;
        if (item.icon_color) {
          const c = String(item.icon_color).trim();
          this.icon_color = c.startsWith('#') ? c : `#${c}`;
        } else {
          this.icon_color = pair.color;
        }
      }
      this.currency_id = this.currenciesList.filter(i => i.id === item.currency_id)[0];
      this.group_id = this.account_groups.data.filter(i => i.id === item.group_id);
      this.balance = !!item.balance ? +item.balance : '0.00';
      this.balance = item.balance.toFixed(item.currency.decimals);
      this.percent = item.percent;
      this.need_check = item.need_check;
      this.auto_confirm = item.auto_confirm;
      this.commission = item.commission;
      this.commission_type = item.commission_type;
      this.commission_value = item.commission_value;
      this.capitalization = item.capitalization;
      this.group_capitalization = item.group_capitalization;
      this.is_hidden = item.is_hidden;
    },
    createPermissionsObject() {
      this.memberPermissions = [];
      let addMember = (member) => {
        let {username, email, id} = member.user;
        this.memberPermissions.push({
          id, username, email,
          value: !!this.edit_item ? this.edit_item.permissions.find(member => member.user_id === id).value : true
        })
      }
      this.permissionMembersList.forEach((member) => addMember(member))
    },
    getMembersPermissions() {
      let permissions = {}
      this.memberPermissions.forEach(item => {
        permissions[item.id] = item.value;
      })
      return permissions;
    }
  },
  watch: {
    'current_space.dirty': {
      immediate: true,
      handler: function (newVal) {
        if (!newVal) {
          this.currency_id = this.currenciesList.filter(i => i.id === this.current_space.data.currency_id)[0];
        }
      }
    },
    edit_item: {
      immediate: true,
      handler(newVal) {
        if (!!newVal) {
          this.setEditFields(newVal);
        }
      },
    },
    'account_groups.data': {
      handler: function (newVal) {
        if (this.group_id !== null && newVal.length === 1) {
          this.group_id = {id: null, title: 'Без группы'};
        }
        if (!newVal.some(i => i.id === this.group_id.id)) {
          this.group_id = {id: null, title: 'Без группы'};
        }
      }
    }
  },
  mounted() {
    if (this.currenciesList.length === 0) {
      this.getCurrencies();
    }
    if (!this.edit_item) {
      this.type = this.type_list[0]
    }
    if (this.showMembersPermissions) {
      this.createPermissionsObject();
    }
  },
  i18n: {
    messages: {
      en: {
        formTitle: 'Create account',
        typeLabel: 'Account type',
        accountNameLabel: 'Account name',
        iconPickText: 'Pick an icon for account',
        currencyLabel: 'Account currency',
        groupSelectLabel: 'Account group',
        createGroup: 'Create a group',
        currentBalanceLabel: 'Account balance',
        creditPercentLabel: 'Interest of a loan',
        checkCommission: 'Account commission is enabled',
        commissionTypePercent: 'Percentage of the amount',
        commissionTypeFix: 'Fixed commission',
        commissionAmountLabel: 'Amount of commission',
        checkCheckImportLabel: 'Need to check imported operations',
        checkAutoConfirm: 'Auto confirmation of recurring operations',
        checkCapitalization: 'Accounting for the account balance in the total capital',
        checkGroupCapitalization: 'Accounting for the account balance in an account group',
        checkIsHidden: 'Hide in the list of accounts',
        membersPermissions: 'Users',
        addAccount: 'Create account',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        removeAccount: 'Delete account'
      },
      ru: {
        formTitle: 'Добавление счета',
        typeLabel: 'Тип счета',
        accountNameLabel: 'Название счета',
        iconPickText: 'Выберите иконку для счета',
        currencyLabel: 'Валюта счета',
        groupSelectLabel: 'Группа счетов',
        createGroup: 'Создать группу',
        currentBalanceLabel: 'Баланс счета',
        creditPercentLabel: 'Процент по кредиту',
        checkCommission: 'Наличие комиссий за переводы',
        commissionTypePercent: 'Процент от суммы',
        commissionTypeFix: 'Фиксированная комиссия',
        commissionAmountLabel: 'Размер комиссии',
        checkCheckImportLabel: 'Проверка импортированных операций',
        checkAutoConfirm: 'Автоподтверждение периодических операций',
        checkCapitalization: 'Учет баланса счета в общем капитале',
        checkGroupCapitalization: 'Учет баланса счета в группе счетов',
        checkIsHidden: 'Скрыть в списке счетов',
        membersPermissions: 'Пользователи',
        addAccount: 'Добавить счет',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        removeAccount: 'Удалить счет'
      }
    }
  }
}
</script>
